import axios from "axios";

/**
 * 入場制御（待合室）のクライアント。 issue #2992 / 報告書 §8 No.11
 *
 * ■ 端末が持つもの
 *   - **整理番号 `n`**: 一度もらったら変えない。リロード・複数タブで並び直させないため。
 *   - **入場トークン**: 有効期限 30 分（手続き制限時間と同じ）。全 API に `X-Admit-Token` で付ける。
 *   どちらも `localStorage` に保存する。Cookie はサーバー側でも並行して発行しており、
 *   ヘッダを付けられない経路（OGP クローラ等）の保険になっている。
 *
 * ■ ★localStorage が使えない環境（プライベートブラウズ等）でも落とさない
 *   保存できないだけで手続きは続けられる（トークンはメモリに持つ）。
 */

const KEY_TICKET = "ots_queue_ticket";
const KEY_TOKEN = "ots_admit_token";
const KEY_TOKEN_EXP = "ots_admit_token_exp";

/** localStorage が使えないときのフォールバック（このタブの間だけ有効） */
const memory: {[key: string]: string} = {};
/** 一度でも localStorage が例外を投げたらメモリへ切り替える（プライベートブラウズ等） */
let storageUsable = true;

const read = (key: string): string | undefined => {
  if (storageUsable) {
    try {
      return window.localStorage.getItem(key) ?? undefined;
    } catch {
      storageUsable = false;
    }
  }
  return memory[key];
};

const write = (key: string, value: string): void => {
  if (storageUsable) {
    try {
      window.localStorage.setItem(key, value);
      return;
    } catch {
      storageUsable = false;
    }
  }
  memory[key] = value;
};

const remove = (key: string): void => {
  delete memory[key];
  if (!storageUsable) {
    return;
  }
  try {
    window.localStorage.removeItem(key);
  } catch {
    storageUsable = false;
  }
};

/**
 * 保持している**署名付き整理券**（2026-09-03 レビュー §2-E）。
 *
 * ★以前は整理番号（数値）をそのまま保存し、サーバーへ自己申告していた。
 *   `{n: 1}` を送るだけで先頭に割り込めたため、サーバーが署名した整理券を持つ形に変えた。
 *   表示用の番号は応答の `n` を使う（保存しない）。
 */
export const getTicket = (): string | undefined => {
  const v = read(KEY_TICKET);
  return v && v !== "" ? v : undefined;
};

export const setTicket = (ticket: string): void => {
  if (ticket) {
    write(KEY_TICKET, ticket);
  }
};

export const clearTicket = (): void => remove(KEY_TICKET);

/** 保持している入場トークン（期限切れなら undefined） */
export const getAdmitToken = (): string | undefined => {
  const token = read(KEY_TOKEN);
  if (!token) {
    return undefined;
  }
  const exp = Number(read(KEY_TOKEN_EXP) ?? 0);
  if (Number.isFinite(exp) && exp > 0 && exp <= Date.now()) {
    return undefined;
  }
  return token;
};

/**
 * トークンを保存する。
 * ★期限はトークン本体（JWT ではない独自形式）の payload から読む。
 *   検証はサーバーが署名で行うので、ここでは「期限切れを画面側で先に気づく」ためだけに使う。
 */
export const setAdmitToken = (token: string): void => {
  write(KEY_TOKEN, token);
  try {
    const payloadB64 = token.split(".")[0];
    const json = JSON.parse(
        decodeURIComponent(escape(window.atob(payloadB64.replace(/-/g, "+").replace(/_/g, "/"))))
    ) as {exp?: number};
    if (typeof json.exp === "number") {
      write(KEY_TOKEN_EXP, String(json.exp));
    }
  } catch {
    // 読めなくても致命ではない（サーバーが 401 を返せば待機画面へ戻る）
  }
};

/**
 * 保存しているトークンの失効時刻（ミリ秒 epoch）。 2026-09-04
 * ★「期限が来たのに再発行されていない＝絶対上限に達した」を画面側で気づくために使う。
 */
export const getAdmitTokenExp = (): number | undefined => {
  const exp = Number(read(KEY_TOKEN_EXP) ?? 0);
  return Number.isFinite(exp) && exp > 0 ? exp : undefined;
};

export const clearAdmitToken = (): void => {
  remove(KEY_TOKEN);
  remove(KEY_TOKEN_EXP);
};

/** サーバーの応答（functions/src/functions/queue.ts と対応） */
export type QueueStatus = {
  enabled: boolean;
  n: number;
  /** 署名付き整理券（§2-E）。次回の enter / status に提示する */
  ticket?: string;
  position: number;
  admitted: boolean;
  token?: string;
  etaSec?: number;
  pollSec: number;
  heartbeatMin: number;
  message?: {ja?: string; en?: string; zh?: string};
};

/**
 * 待合室 API のクライアント。
 *
 * ★`withCredentials` を付けてはいけない（2026-09-03 レビュー §2-B）。
 *   関数側の CORS 設定（`functions/src/modules/request.ts`）に `credentials: true` が無いため、
 *   付けるとブラウザが応答を遮断する。すると `enter` が例外になり fail-open で素通し扱いになり、
 *   ON のときは全 API が 401、priority 再入場も同じ理由で失敗する。
 *   Cookie は「ヘッダを付けられない経路の保険」なので、送れなくても手続きは成立する
 *   （サーバーの `Set-Cookie` はそのまま残す）。
 */
const client = () =>
  axios.create({
    baseURL: process.env.REACT_APP_FUNCTIONS_BASE_URL,
    timeout: 15000,
    headers: {"Content-Type": "application/json"},
  });

/** 応答を端末へ反映する（整理券とトークン） */
const persist = (data: QueueStatus): QueueStatus => {
  if (data.ticket) {
    setTicket(data.ticket);
  }
  if (data.token) {
    setAdmitToken(data.token);
  }
  return data;
};

/**
 * 列に並ぶ（対象外なら即時トークン）。
 *
 * ★事業者ID（`sellerId`）は送らない（2026-09-03 再レビュー R6）。
 *   クライアントの自己申告でしか届かず、省略するだけで対象外にできたため、
 *   事業者単位の指定（`targetSellerIds`）ごと廃止した。対象はプラン単位で指定する。
 *
 * @param planId   プランID（許可リストの判定に使う）
 * @param priority トークン期限切れからの再入場か（列の先頭側へ）
 */
export const enterQueue = async (
  planId?: string,
  priority = false
): Promise<QueueStatus> => {
  const res = await client().post("/queue/enter", {
    planId,
    // ★署名付き整理券を渡す。サーバーは検証できたときだけ再発番しない（§2-E）
    ticket: getTicket(),
    priority,
    // ★priority は「直前まで入場していた」証明が要る。
    //   期限切れで捨てる直前のトークンを添える（§2-E）
    expiredToken: priority ? getLastToken() : undefined,
  });
  return persist(res.data as QueueStatus);
};

/** 順番の確認。★整理券（署名付き）を提示する */
export const fetchQueueStatus = async (ticket: string): Promise<QueueStatus> => {
  const res = await client().get(`/queue/status?ticket=${encodeURIComponent(ticket)}`);
  return persist(res.data as QueueStatus);
};

/**
 * 手続き中であることの通知。
 *
 * ★サーバーは**トークンを再発行して返す**（スライド。2026-09-02 レビュー指摘）。
 *   手続きが長引いてトークンだけ先に切れると、操作中の利用者が 401 で待合室へ戻される。
 *   受け取ったトークンは保存して以降のリクエストに使う。
 *
 * ★失敗しても無視する（同時滞在数の推定に使うだけで、手続きを止めるものではない）。
 */
export const sendHeartbeat = async (): Promise<void> => {
  const token = getAdmitToken();
  if (!token) {
    return;
  }
  try {
    const res = await client().post("/queue/heartbeat", {}, {headers: {"X-Admit-Token": token}});
    const renewed = (res.data as {token?: string} | undefined)?.token;
    // ★応答が返るまでの間にトークンが消された・差し替わっていたら保存しない（2026-09-06 再レビュー）。
    //   無操作で打ち切って `clearAdmitToken()` した直後に遅れて応答が返ると、
    //   **捨てたはずのトークンが復活**して枠を持ち続けてしまう。
    if (renewed && getAdmitToken() === token) {
      setAdmitToken(renewed);
    }
  } catch {
    // no-op
  }
};

/* ------------------------------------------------------------------ *
 * 滞在通知の担当（プランページとそれ以外で二重に送らない）
 * ------------------------------------------------------------------ */

/**
 * プランページ（`useWaitingRoom`）が滞在通知を担当しているか。 2026-09-06 再レビュー
 *
 * ★プランページでは `FormSessionProvider` と `useWaitingRoom` の**2 本**が heartbeat を送っていた。
 *   `useWaitingRoom` 側は「無操作なら送らない」を持っているが、Provider 側は送り続けるため、
 *   放置されたプランページが `maxActive` の枠を持ち続ける。
 *   プランページがマウントされている間は Provider を止める（それ以外の画面は従来どおり）。
 */
let waitingRoomOwnsHeartbeat = false;

export const setWaitingRoomOwnsHeartbeat = (owns: boolean): void => {
  waitingRoomOwnsHeartbeat = owns;
};

export const isWaitingRoomOwningHeartbeat = (): boolean => waitingRoomOwnsHeartbeat;

/**
 * 手続きの**絶対上限を 1 回だけ延ばす**（`POST /queue/extend`）。 2026-09-04
 *
 * ★プラン選択で入場トークンの上限（`tokenTtlMin` 60 分）を使い切ると、
 *   フォームの記入中に切れて待合室へ戻される。プランページの「次へ」でここを呼び、
 *   フォームのぶん（`extendMin` 既定 60 分）を足す。**延長は通算 1 回だけ**。
 *
 * ★失敗しても遷移は止めない（待合室 OFF なら何も起きない）。
 */
export const extendSession = async (): Promise<void> => {
  const token = getAdmitToken();
  if (!token) {
    return;
  }
  try {
    const res = await client().post("/queue/extend", {}, {headers: {"X-Admit-Token": token}});
    const renewed = (res.data as {token?: string} | undefined)?.token;
    if (renewed) {
      setAdmitToken(renewed);
    }
  } catch {
    // no-op（延長できなくても手続きは続けられる）
  }
};

/* ------------------------------------------------------------------ *
 * 401 ADMIT_REQUIRED の共通ハンドラ
 * ------------------------------------------------------------------ */

/**
 * 入場トークンが失効したときに呼ばれるハンドラ。 issue #2992・レビュー指摘
 *
 * ★`useApi` と `apiClient` / `signalApiClient` の**両方**から呼ぶこと。
 *   以前は `apiClient` 側がトークンを消すだけで再入場を通知しておらず、
 *   検索系 API（ホテル一覧・フライト・プラン）の多くが待合室へ戻れず
 *   「取得できませんでした」の表示で止まっていた。
 */
type AdmitRequiredHandler = () => void;
let onAdmitRequired: AdmitRequiredHandler | undefined;

/**
 * 画面別のハンドラが無いとき（プランページ以外）の受け皿。 §2-H
 * ★これが無いと、Form4 の `/price` などで 401 を受けたときにトークンだけ消えて
 *   再試行しても 401 のまま＝手続き中の利用者が詰む。
 */
type DefaultAdmitRequiredHandler = (planData?: {id?: string; searchParams?: unknown}) => void;
let defaultOnAdmitRequired: DefaultAdmitRequiredHandler | undefined;
/** 既定ハンドラへ渡すための、直近の手続きデータ */
let currentPlanData: {id?: string; searchParams?: unknown} | undefined;

export const setOnAdmitRequired = (handler?: AdmitRequiredHandler): void => {
  onAdmitRequired = handler;
};

export const setDefaultOnAdmitRequired = (handler?: DefaultAdmitRequiredHandler): void => {
  defaultOnAdmitRequired = handler;
};

/** 既定ハンドラが戻り先を組み立てられるよう、手続きデータを控える（§2-H） */
export const setCurrentPlanData = (data?: {id?: string; searchParams?: unknown}): void => {
  currentPlanData = data;
};

/**
 * 期限切れで捨てた直前のトークン。priority 再入場の「証明」に使う（§2-E）。
 * ★メモリだけに持つ（localStorage には残さない）。
 */
let lastToken: string | undefined;

export const getLastToken = (): string | undefined => lastToken;

/**
 * 捨てる直前のトークンを控える。 2026-09-06
 *
 * ★`clearAdmitToken()` の**前**に必ず呼ぶこと。これが無いと priority 再入場の証明
 *   （`expiredToken`）を出せず、`cap` の内側でトークンだけ切れた人が**列の後ろに回される**。
 * ★期限切れだと `getAdmitToken()` は undefined を返すので、生の保存値も見る。
 *   どちらも無ければ既存の控えを消さない（上書きで証明を失わない）。
 */
export const rememberLastToken = (): void => {
  lastToken = getAdmitToken() ?? read(KEY_TOKEN) ?? lastToken;
};

/** 401 ADMIT_REQUIRED を受けたときの共通処理。トークンを捨てて再入場を促す。 */
export const handleAdmitRequired = (): void => {
  // ★消す前に控える。これが無いと priority 再入場の証明を出せない（§2-E）
  rememberLastToken();
  clearAdmitToken();
  if (onAdmitRequired) {
    // プランページ（useWaitingRoom）が受ける
    onAdmitRequired();
    return;
  }
  // ★プランページ以外。入力を保ったままプランページへ戻す（§2-H）
  defaultOnAdmitRequired?.(currentPlanData);
};

/**
 * `status` が「整理券を出し直せ」と言っているか（400 `TICKET_REQUIRED`）。 2026-09-03 再レビュー R5
 *
 * ★世代を進めた／整理券が 24 時間で切れた場合、同じ整理券で何度確認しても直らない。
 *   区別せずにバックオフ再試行すると**永久に待機画面のまま**になる。
 */
export const isTicketRequired = (error: unknown): boolean => {
  const res = (error as {response?: {status?: number; data?: {error?: string}}} | undefined)?.response;
  return res?.status === 400 && res?.data?.error === "TICKET_REQUIRED";
};

/** レスポンスが「入場トークンを要求されている」ものか */
export const isAdmitRequired = (status?: number, data?: unknown): boolean =>
  status === 401 && (data as {code?: string} | undefined)?.code === "ADMIT_REQUIRED";
